/**
 * Staff Work — My Projects. The projects the current user leads (project head)
 * or is an active resource on. Active scope = open projects, grouped into a
 * Planning / Active / On Hold kanban; archive (History) = closed projects.
 *
 * Opening a card shows the project. The HEAD (project_manager_id === me) gets
 * the full project modal; every other member/resource gets a limited view —
 * only the WBS tab (WBS Grid + Kanban), no Basic Details / Resources / edits.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { FolderKanban, Loader2, PauseCircle, Archive } from 'lucide-react';
import Badge from '../../../components/common/Badge';
import { formatDate } from '../../../components/common/FormControl';
import Skeleton from '../../../components/common/Skeleton';
import NoDataFound from '../../../components/common/NoDataFound';
import { statusVariant } from '../../../utils/statusVariant';
import { useAuth } from '../../../contexts/AuthContext';
import { projectService, Project } from '../../../services/projectService';
import { ProjectDetailsModal } from '../../admin/project-management/ProjectDetailsModal';
import { isClosedProject } from './workArchive';
import '../../../components/layout/ModuleLayout.css';

interface BucketDef {
  key: string;
  label: string;
  icon: React.ReactNode;
  accent: string;
  emptyText: string;
  filter: (p: Project) => boolean;
}

// 3-column kanban for open projects. Closed ones live in History only.
const BUCKETS: BucketDef[] = [
  { key: 'planning', label: 'Planning', icon: <FolderKanban size={13} />, accent: '#f59e0b', emptyText: 'Nothing in planning.', filter: p => p.status === 'DRAFT' },
  { key: 'active', label: 'Active', icon: <Loader2 size={13} />, accent: '#0284c7', emptyText: 'No active projects.', filter: p => p.status === 'ACTIVE' },
  { key: 'on_hold', label: 'On Hold', icon: <PauseCircle size={13} />, accent: '#8b5cf6', emptyText: 'None on hold.', filter: p => p.status === 'ON_HOLD' },
];

// Display per Entity Settings → date_format.
const fmtDate = (d?: string | null) => formatDate(d);

const WorkProjectsBody: React.FC<{ scope?: 'active' | 'archive'; readOnly?: boolean }> = ({ scope = 'active' }) => {
  const { state } = useAuth();
  const myId = state.user?.id;
  const isArchive = scope === 'archive';

  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [openProject, setOpenProject] = useState<Project | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await projectService.getMyProjects(1, 100);
        if (!cancelled) setProjects(r.data || []);
      } catch {
        if (!cancelled) setProjects([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const visible = useMemo(
    () => projects.filter(p => isArchive ? isClosedProject(p.status) : !isClosedProject(p.status)),
    [projects, isArchive],
  );

  const isHead = (p: Project) => !!myId && p.project_manager_id === myId;

  const Card: React.FC<{ p: Project }> = ({ p }) => (
    <div
      className="action-card"
      role="button"
      tabIndex={0}
      data-testid={`BTN_work_projects_row_${p.id}_open`}
      onClick={() => setOpenProject(p)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenProject(p); } }}
    >
      <div className="action-card__header">
        <div className="action-card__title-block">
          <span className="action-card__subtitle" style={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 'var(--font-weight-heading)' }}>
            {p.project_code || 'PROJECT'} · {isHead(p) ? 'Lead' : 'Member'}
          </span>
          <span className="action-card__title">{p.name}</span>
          {p.description && <span className="action-card__subtitle">{p.description}</span>}
        </div>
        <div className="action-card__badge">
          <Badge variant={statusVariant(p.status)} size="xs">{(p.status || '').replace('_', ' ')}</Badge>
        </div>
      </div>
      <div className="action-card__meta">
        <span className="action-card__meta-item">{fmtDate(p.baseline_start_date)} → {fmtDate(p.baseline_finish_date)}</span>
      </div>
    </div>
  );

  // History — flat table of closed projects.
  if (isArchive) {
    return (
      <div className="netbody-card flex flex-col h-full">
        <div className="netbody-content">
          <div className="scroll-container-nofilters">
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 16 }}>
                {[1, 2, 3, 4].map(i => <Skeleton.Box key={i} width="100%" height={16} />)}
              </div>
            ) : visible.length === 0 ? (
              <NoDataFound title="No History" message="No closed projects yet." />
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border-color, #e5e7eb)', textAlign: 'left' }}>
                      {['Status', 'Code', 'Project', 'My Role', 'Start', 'Finish'].map(h => (
                        <th key={h} style={{ padding: '8px 12px', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted, #6b7280)', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map(p => (
                      <tr
                        key={p.id}
                        data-testid={`BTN_work_projects_row_${p.id}_open`}
                        onClick={() => setOpenProject(p)}
                        style={{ borderBottom: '1px solid var(--border-color, #f3f4f6)', cursor: 'pointer' }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'var(--hover-bg, #f9fafb)')}
                        onMouseLeave={e => (e.currentTarget.style.background = '')}
                      >
                        <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}><Badge variant={statusVariant(p.status)} size="xs">{(p.status || '').replace('_', ' ')}</Badge></td>
                        <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: 'var(--text-secondary, #374151)' }}>{p.project_code || '—'}</td>
                        <td style={{ padding: '10px 12px', fontWeight: 600 }}>{p.name}</td>
                        <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{isHead(p) ? 'Lead' : 'Member'}</td>
                        <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDate(p.baseline_start_date)}</td>
                        <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDate(p.baseline_finish_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
        {openProject && (
          <ProjectDetailsModal
            project={openProject}
            onClose={() => setOpenProject(null)}
            canManage={isHead(openProject)}
            initialTab={isHead(openProject) ? 'DETAILS' : 'WBS'}
          />
        )}
      </div>
    );
  }

  // Action Centre — Planning / Active / On Hold kanban.
  return (
    <div className="netbody-card flex flex-col h-full">
      <div className="netbody-content">
        <div className="scroll-container">
          {!loading && visible.length === 0 ? (
            <NoDataFound title="No Projects" message="You don't lead or belong to any open project yet." />
          ) : (
            <div className="bucket-grid bucket-grid--3 pt-2">
              {BUCKETS.map(bucket => {
                const items = loading ? [] : visible.filter(bucket.filter);
                return (
                  <div key={bucket.key} className="bucket-panel" style={{ '--accent': bucket.accent } as React.CSSProperties}>
                    <div className="bucket-panel-header">
                      <span className="bucket-panel-icon">{bucket.icon}</span>
                      <span className="bucket-panel-title">{bucket.label.toUpperCase()}</span>
                      <span className="bucket-panel-count">{loading ? '…' : items.length}</span>
                    </div>
                    <div className="bucket-panel-scroll">
                      {loading ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '8px 4px' }}>
                          {[1, 2].map(i => <Skeleton.Box key={i} width="100%" height={64} />)}
                        </div>
                      ) : items.length === 0 ? (
                        <div className="bucket-empty-state">
                          <Archive size={28} className="bucket-empty-icon" />
                          <span className="bucket-empty-text">{bucket.emptyText}</span>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '8px 4px' }}>
                          {items.map(p => <Card key={p.id} p={p} />)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {openProject && (
        <ProjectDetailsModal
          project={openProject}
          onClose={() => setOpenProject(null)}
          canManage={isHead(openProject)}
          initialTab={isHead(openProject) ? 'DETAILS' : 'WBS'}
        />
      )}
    </div>
  );
};

export default WorkProjectsBody;
